import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Table, Row, Col, Card, CardBody, Input, Badge, Pagination, PaginationItem, PaginationLink, Label } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Select from "react-select";

// MUI DatePicker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const FarmerList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const user = getCookie("user_data") ? JSON.parse(getCookie("user_data")) : {};
    const isAdmin = user?.role === 'admin';

    const [farmers, setFarmers] = useState([]);

    // 🔥 Machan, keys updated to exactly match the PHP API summary response payload
    const [summary, setSummary] = useState({
        total: 0,
        farmers: 0,
        traders: 0,
        active: 0,
        inactive: 0
    });

    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_records: 0 });

    // 🔥 Added 'type', 'sort_by' and 'sort_order' inside state array to support robust full API filters
    const [filters, setFilters] = useState({
        name: "",
        phone: "",
        type: null,
        status: null,
        has_bank: null,
        sort_by: "id",
        sort_order: "DESC",
        page: 1,
        limit: 10
    });

    const statusOptions = [
        { value: "1", label: "Active" },
        { value: "0", label: "Inactive" }
    ];

    const bankOptions = [
        { value: "1", label: "With Bank Info" },
        { value: "0", label: "Missing Bank Info" }
    ];

    // 🔥 Dynamic dropdown option mappings for matching 'farmer' and 'trader' filtering backend query
    const typeOptions = [
        { value: "farmer", label: "Farmer" },
        { value: "trader", label: "Trader" }
    ];

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '45px',
            borderRadius: '8px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: '#2e4a3d' }
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    const loadFarmers = async () => {
        dispatch(loadingTrue());
        try {
            const params = {
                page: filters.page,
                limit: filters.limit,
                name: filters.name,
                phone: filters.phone,
                sort_by: filters.sort_by,
                sort_order: filters.sort_order,
                type: filters.type ? filters.type.value : "",
                status: filters.status ? filters.status.value : "",
                has_bank: filters.has_bank ? filters.has_bank.value : ""
            };

            const queryParams = new URLSearchParams(params).toString();
            const res = await Handler({ method: "GET", url: `/farmer/list.php?${queryParams}` });

            if (res?.success) {
                setFarmers(res.data || []);
                if (res.summary) {
                    setSummary(res.summary);
                }
                setPagination({
                    current_page: res.current_page || 1,
                    total_pages: res.total_pages || 1,
                    total_records: res.total_records || 0
                });
            } else {
                toast.error(res?.message || "Failed to fetch data");
            }
        } catch (err) {
            toast.error("Server connection error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const handleStatusToggle = async (id, currentStatus) => {
        if (!isAdmin) return;

        const newStatus = currentStatus === 1 ? 0 : 1;
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to ${newStatus === 1 ? 'Activate' : 'Deactivate'} this record?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#2e4a3d',
            confirmButtonText: 'Yes, update it!'
        });

        if (result.isConfirmed) {
            dispatch(loadingTrue());
            try {
                const res = await Handler({
                    method: "POST",
                    url: "/farmer/update.php",
                    data: { id: id, status: newStatus }
                });

                if (res?.success) {
                    toast.success(`Status updated successfully!`);
                    loadFarmers();
                } else {
                    toast.error(res?.message || "Update failed");
                }
            } catch (e) {
                toast.error("Error updating status");
            } finally {
                dispatch(loadingFalse());
            }
        }
    };

    // 🔥 Added effect listeners for automatic execution hook mapping when parameters are refreshed by inputs
    useEffect(() => {
        loadFarmers();
    }, [filters.page, filters.status, filters.has_bank, filters.type, filters.sort_by, filters.sort_order]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target?.name]: e.target.value, page: 1 });
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter') loadFarmers();
    };

    // Safe cookie helper internal execution context
    function getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-1 bg-light min-vh-100">
                <div className="page-header mb-4">
                    <h6 className="text-muted small">Management &gt; Directory</h6>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h3 className="fw-bold text-dark m-0">Profiles Ledger</h3>
                            <p className="text-muted small">
                                Total: <b>{summary.total}</b> |
                                Farmers: <span className="text-primary fw-bold">{summary.farmers}</span> |
                                Traders: <span className="text-warning fw-bold">{summary.traders}</span> |
                                Active: <span className="text-success fw-bold">{summary.active}</span>
                            </p>
                        </div>
                        {isAdmin && (
                            <Button color="danger" className="px-4 shadow-sm fw-bold" onClick={() => navigate("/farmers/add")}>
                                <i className="bx bx-plus me-1"></i> Add New Record
                            </Button>
                        )}
                    </div>
                </div>

                {/* Filters Section */}
                <Card className="border-0 shadow-sm mb-4 rounded-4">
                    <CardBody className="p-3">
                        <Row className="g-2 align-items-end">
                            <Col md={2}>
                                <Label className="small fw-bold">Search Name</Label>
                                <Input style={{ height: '45px' }} name="name" placeholder="Search name..." value={filters?.name} onChange={handleFilterChange} onKeyDown={handleSearch} />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">Phone Number</Label>
                                <Input style={{ height: '45px' }} name="phone" placeholder="Phone..." value={filters?.phone} onChange={handleFilterChange} onKeyDown={handleSearch} />
                            </Col>
                            {/* 🔥 New Filter Integration: Profile Type Dropdown Selector */}
                            <Col md={2}>
                                <Label className="small fw-bold">Profile Type</Label>
                                <Select
                                    options={typeOptions}
                                    value={filters?.type}
                                    onChange={(opt) => setFilters({ ...filters, type: opt, page: 1 })}
                                    styles={customSelectStyles}
                                    isClearable
                                    placeholder="All Types"
                                />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">Bank Status</Label>
                                <Select
                                    options={bankOptions}
                                    value={filters?.has_bank}
                                    onChange={(opt) => setFilters({ ...filters, has_bank: opt, page: 1 })}
                                    styles={customSelectStyles}
                                    isClearable
                                    placeholder="Select..."
                                />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">Account Status</Label>
                                <Select
                                    options={statusOptions}
                                    value={filters?.status}
                                    onChange={(opt) => setFilters({ ...filters, status: opt, page: 1 })}
                                    styles={customSelectStyles}
                                    isClearable
                                    placeholder="Select..."
                                />
                            </Col>
                            <Col md={2} className="text-end d-flex">
                                <Button color="primary" className="w-100 me-1" style={{ height: '45px', background: '#2e4a3d', border: 'none' }} onClick={loadFarmers}>
                                    <i className="bx bx-filter-alt me-1"></i> Filter
                                </Button>
                                <Button color="light" className="border" style={{ height: '45px' }} onClick={() => setFilters({ name: "", phone: "", type: null, status: null, has_bank: null, sort_by: "id", sort_order: "DESC", page: 1, limit: 10 })}>
                                    Reset
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Table Section */}
                <Card className="border-0 shadow-sm rounded-4">
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light text-muted small text-uppercase">
                                <tr>
                                    <th className="ps-4" style={{ width: '80px' }}>S.No</th>
                                    <th>Profile Details</th>
                                    <th>Identity & Contact</th>
                                    <th>Bank Information</th>
                                    <th>Status Switch</th>
                                    <th>Created On</th>
                                    {isAdmin && <th className="text-center">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {farmers.length > 0 ? (
                                    farmers.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="ps-4">{(pagination.current_page - 1) * filters.limit + index + 1}</td>
                                            <td>
                                                {/* 🔥 Fixed mapping: points to item.contact.name and uppercased cleanly */}
                                                <div className="fw-bold text-dark">{item?.contact?.name?.toUpperCase()}</div>
                                                <small className="text-muted text-capitalize">
                                                    <i className="bx bx-map me-1"></i>{item?.address || "No address details available"}
                                                </small>
                                            </td>
                                            <td>
                                                <div><i className="bx bx-phone me-1 text-muted"></i>{item?.contact?.phone}</div>
                                                {/* 🔥 Render explicit badge representation for checking farmer or trader classification type */}
                                                <Badge color={item?.contact?.type === 'farmer' ? 'success' : 'info'} className="text-capitalize small mt-1">
                                                    {item?.contact?.type}
                                                </Badge>
                                            </td>
                                            <td>
                                                {item?.bank?.has_bank ? (
                                                    <div>
                                                        <div className="small text-dark fw-bold">{item?.bank?.account_no}</div>
                                                        <Badge color="light" className="text-success border fw-bold p-1 mt-1">
                                                            <i className="bx bx-check-double me-1"></i>{item?.bank?.ifsc_code}
                                                        </Badge>
                                                    </div>
                                                ) : (
                                                    <Badge color="light" className="text-danger border small">
                                                        <i className="bx bx-error-circle me-1"></i>Missing Bank Records
                                                    </Badge>
                                                )}
                                            </td>
                                            <td>
                                                <div className="form-check form-switch d-flex align-items-center">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        checked={item.status.value === 1}
                                                        disabled={!isAdmin}
                                                        onChange={() => handleStatusToggle(item.id, item.status.value)}
                                                        style={{ cursor: isAdmin ? 'pointer' : 'default' }}
                                                    />
                                                    <Badge color={item.status.value === 1 ? "success" : "danger"} pill className="ms-2">
                                                        {item.status.label}
                                                    </Badge>
                                                </div>
                                            </td>
                                            <td>{dayjs(item.created_at).format('DD-MM-YYYY')}</td>
                                            {isAdmin && (
                                                <td className="text-center">
                                                    <Button color="light" size="sm" className="rounded-3 shadow-sm border" onClick={() => navigate(`/farmers/${item.id}`)}>
                                                        <i className="bx bx-edit text-primary fs-5"></i>
                                                    </Button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={isAdmin ? "7" : "6"} className="text-center py-5 text-muted italic">
                                            No matched database records found for chosen parameters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* Pagination Section */}
                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination className="pagination-rounded shadow-sm">
                            {/* Previous Button */}
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                            </PaginationItem>

                            {/* Dynamic Page Rendering */}
                            {[...Array(pagination?.total_pages)].map((_, i) => {
                                const pageNumber = i + 1;
                                const isCurrent = pageNumber === filters.page;

                                // Show first, last, current, and immediate neighbors
                                const shouldShow =
                                    pageNumber === 1 ||
                                    pageNumber === pagination?.total_pages ||
                                    (pageNumber >= filters.page - 1 && pageNumber <= filters.page + 1);

                                if (shouldShow) {
                                    return (
                                        <PaginationItem active={isCurrent} key={i}>
                                            <PaginationLink onClick={() => setFilters({ ...filters, page: pageNumber })}>
                                                {pageNumber}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                } else if (
                                    pageNumber === filters.page - 2 ||
                                    pageNumber === filters.page + 2
                                ) {
                                    // Add ellipsis for gaps
                                    return (
                                        <PaginationItem disabled key={i}>
                                            <PaginationLink>...</PaginationLink>
                                        </PaginationItem>
                                    );
                                }
                                return null;
                            })}

                            {/* Next Button */}
                            <PaginationItem disabled={filters.page >= pagination?.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default FarmerList;